import React, { ReactNode, createContext, useContext } from 'react'

import type { SxProps } from '@mui/material/styles'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'

import { useIsMobile } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import TableCell from 'components_new/atoms/Table/TableCell'
import Text from 'components_new/atoms/Text'

import { FlattenedNode } from './types'
interface NameCellContextValue {
  anyRowHasChildren: boolean
  disableDrag?: boolean
  editMode?: boolean
  hasChildren: boolean
  listeners?: SyntheticListenerMap
  node: FlattenedNode
  onToggleExpand: (id: string) => void
}

export const NameCellContext = createContext<NameCellContextValue | null>(null)

export const useTreeDndTable = () => {
  const context = useContext(NameCellContext)

  if (!context) {
    throw new Error('useTreeDndTable must be used within a TreeDndTable')
  }

  return { editMode: context.editMode }
}

interface Props {
  children?: ReactNode
  sx?: SxProps
}

const NameCell = React.forwardRef<HTMLTableCellElement, Props>((props, ref) => {
  const { children, sx } = props

  const context = useContext(NameCellContext)
  const isMobile = useIsMobile()

  const INDENT_SIZE = isMobile ? 8 : 24

  if (!context) {
    return null
  }

  const {
    anyRowHasChildren,
    disableDrag,
    editMode,
    hasChildren,
    listeners,
    node
  } = context

  return (
    <TableCell
      ref={ref}
      sx={{
        px: isMobile ? 0 : undefined,
        py: isMobile ? 1 : undefined,
        ...sx
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
          pl: `${node.depth * INDENT_SIZE}px`
        }}
      >
        {editMode && !disableDrag ? (
          <Box
            sx={{
              alignItems: 'center',
              color: 'action.disabled',
              cursor: 'grab',
              display: 'flex',
              '&:active': { cursor: 'grabbing' },
              '&:hover': { color: 'action.active' }
            }}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <Icon fontSize="small" name="DragHandleVertical" />
          </Box>
        ) : null}
        {hasChildren ? (
          <IconButton disablePadding size="small">
            <Icon
              fontSize="small"
              name={
                node.isExpanded ? 'KeyboardArrowDown' : 'KeyboardArrowRight'
              }
            />
          </IconButton>
        ) : anyRowHasChildren ? (
          <Box sx={{ width: 20 }} />
        ) : null}
        {children ?? <Text variant="body2">{node.data.name}</Text>}
      </Box>
    </TableCell>
  )
})

NameCell.displayName = 'NameCell'
export default NameCell
